import React, { useCallback, useEffect, useRef, useState } from 'react';

// Material UI
import {
    Box,
    Button,
    Chip,
    Container,
    Divider,
    Link,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';

// Utility
import { useAuth } from 'context/auth';
import fetchExpresso from 'utility/fetchExpresso';
import { SESSION_EXPIRED_REASON } from 'utility/sessionExpiry';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import ReactGA from 'react-ga';

// Images
import logoDark from '../images/logo-dark.png';

export default function Login() {
    const { auth } = useAuth();
    if (auth) return <Redirect to="/" />;

    return (
        <Page>
            <Header>
                <Container maxWidth="lg">
                    <a href="https://frontdoorplus.com/">
                        <Logo alt="FrontDoor+ Logo" src={logoDark} />
                    </a>
                </Container>
            </Header>

            <Container maxWidth="lg" sx={{ flex: 1 }}>
                <Content>
                    <Intro>
                        <HeroTitle component="h1">
                            Ticketing that helps event day feel easier.
                        </HeroTitle>
                        <HeroCopy>
                            FrontDoor+ is a care-driven ticketing partner for live and community
                            events. We help organizers sell tickets, manage gate sales, scan guests
                            in, and understand how their event is performing, with real human
                            support along the way.
                        </HeroCopy>

                        <FeatureChips direction="row" flexWrap="wrap" gap={1}>
                            {[
                                'Online Tickets',
                                'Gate Sales',
                                'Guest Check-In',
                                'Live Reporting',
                                'Real Human Support',
                            ].map((feature) => (
                                <FeatureChip key={feature} label={feature} />
                            ))}
                        </FeatureChips>

                        <DividerStyled />

                        <NewAccount>
                            <SectionTitle component="h2">New to FrontDoor+?</SectionTitle>
                            <SectionCopy>
                                FrontDoor+ is a supported platform, not a self-serve signup tool.
                                Accounts are created after your organization is set up by our team.
                                If you&apos;re planning an event, we&apos;d be happy to learn what
                                you&apos;re working on and show you how FrontDoor+ can help.
                            </SectionCopy>
                            <SecondaryButton
                                href="https://frontdoorplus.com/contact/"
                                variant="outlined"
                            >
                                Start a Conversation
                            </SecondaryButton>
                        </NewAccount>
                    </Intro>

                    <CardRoot elevation={0}>
                        <LoginCard />
                    </CardRoot>
                </Content>
            </Container>

            <Footer>
                <Container maxWidth="lg">
                    <FooterInner>
                        <FooterText>© 2026 FrontDoor+</FooterText>
                        <FooterLink href="https://frontdoorplus.com" underline="none">
                            frontdoorplus.com
                        </FooterLink>
                    </FooterInner>
                </Container>
            </Footer>
        </Page>
    );
}

function LoginCard() {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [mode, setMode] = useState('email');
    const [sentEmail, setSentEmail] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [resendAvailableAt, setResendAvailableAt] = useState(0);
    const { login } = useAuth();
    const location = useLocation();
    const history = useHistory();
    const [emailToken] = useState(() => new URLSearchParams(location.hash.slice(1)).get('token'));
    const [sessionExpired] = useState(
        () => new URLSearchParams(location.search).get('reason') === SESSION_EXPIRED_REASON,
    );

    const completeLogin = useCallback(
        (data) => {
            login(data);
            ReactGA.event({
                category: 'Authentication',
                action: 'Log In',
                label: `${data.hostId ? data.hostId : 'SuperAdmin'}`,
            });
        },
        [login],
    );

    useEffect(() => {
        // Capture once, then remove credentials and transient banners from the URL.
        if (emailToken !== null || new URLSearchParams(location.search).has('reason')) {
            history.replace('/login');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const verifying = useEmailLinkVerification(emailToken, completeLogin, setError);

    async function requestEmail(email) {
        setLoading(true);
        setError('');
        try {
            const res = await fetchExpresso('/apiv2/auth/portal/email', {
                method: 'POST',
                body: { email },
            });
            if (res.status !== 200) {
                setError('Unable to send a sign-in link. Please try again.');
                return;
            }
            setSentEmail(email);
            setMode('sent');
            setResendAvailableAt(Date.now() + 30_000);
        } catch {
            setError('Unable to send a sign-in link. Check your connection and try again.');
        } finally {
            setLoading(false);
        }
    }

    async function onSubmit(e) {
        e.preventDefault();
        if (loading) return;
        if (mode === 'email') {
            await requestEmail(username.trim());
            return;
        }
        setLoading(true);
        setError('');
        try {
            const res = await fetchExpresso('/apiv2/auth/portal', {
                method: 'POST',
                body: { username: username.trim(), password },
            });
            if (res.status !== 200) {
                setError(
                    res.status === 401
                        ? 'Incorrect email/password'
                        : 'Unable to sign in. Please try again.',
                );
                return;
            }
            completeLogin(await res.json());
        } catch {
            setError('Unable to sign in. Check your connection and try again.');
        } finally {
            setLoading(false);
        }
    }

    function changeMode(nextMode) {
        setMode(nextMode);
        setError('');
        setPassword('');
    }

    if (verifying) {
        return (
            <>
                <CardTitle component="h2">Signing you in</CardTitle>
                <CardSubtitle role="status">Verifying your sign-in link…</CardSubtitle>
            </>
        );
    }
    if (mode === 'sent') {
        return (
            <EmailConfirmation
                email={sentEmail}
                error={error}
                loading={loading}
                resendAvailableAt={resendAvailableAt}
                onResend={() => requestEmail(sentEmail)}
                onChangeEmail={() => changeMode('email')}
            />
        );
    }
    return (
        <>
            <CardTitle component="h2">Sign in to your FrontDoor+ Dashboard</CardTitle>
            <CardSubtitle>Use the account provided by the FrontDoor+ team.</CardSubtitle>
            {sessionExpired && (
                <SessionExpiredBanner role="alert">
                    Your session has expired. Please sign in again.
                </SessionExpiredBanner>
            )}
            <Form component="form" onSubmit={onSubmit}>
                <Label component="label" htmlFor="email">
                    Email address
                </Label>
                <AuthTextField
                    id="email"
                    error={Boolean(error)}
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="you@organization.com"
                    type="email"
                    autoComplete="username"
                    required
                    disabled={loading}
                    variant="outlined"
                    fullWidth
                    inputProps={{
                        'aria-label': 'Email address',
                        maxLength: 50,
                    }}
                />
                {mode === 'password' && (
                    <>
                        <Label component="label" htmlFor="password">
                            Password
                        </Label>
                        <AuthTextField
                            id="password"
                            error={Boolean(error)}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="••••••••"
                            type="password"
                            autoComplete="current-password"
                            required
                            disabled={loading}
                            variant="outlined"
                            fullWidth
                            inputProps={{ 'aria-label': 'Password' }}
                        />
                    </>
                )}
                {error && <ErrorText role="alert">{error}</ErrorText>}
                {mode === 'password' && (
                    <InlineLink
                        href={`${process.env.REACT_APP_PUBLIC_URL}/forgotpassword`}
                        underline="none"
                    >
                        Forgot your password?
                    </InlineLink>
                )}
                <PrimaryButton disabled={loading} type="submit" variant="contained">
                    {loading
                        ? mode === 'email'
                            ? 'Sending…'
                            : 'Signing in…'
                        : mode === 'email'
                          ? 'Next'
                          : 'Sign In'}
                </PrimaryButton>
                <ModeLink
                    component="button"
                    type="button"
                    disabled={loading}
                    onClick={() => changeMode(mode === 'email' ? 'password' : 'email')}
                >
                    {mode === 'email' ? 'Login with Password' : 'Login with Email'}
                </ModeLink>
            </Form>
        </>
    );
}

function useEmailLinkVerification(emailToken, completeLogin, setError) {
    const [verifying, setVerifying] = useState(emailToken !== null);
    const verificationRequest = useRef(null);
    useEffect(() => {
        if (emailToken === null) return;
        let active = true;
        // Reuse the request if React re-runs the effect: a one-time token must not be
        // redeemed twice by a single mount (including StrictMode).
        if (!verificationRequest.current) {
            verificationRequest.current = fetchExpresso('/apiv2/auth/portal/email/verify', {
                method: 'POST',
                body: { token: emailToken },
            })
                .catch(() => {
                    throw new Error('Unable to sign in. Check your connection and try again.');
                })
                .then(async (res) => {
                    if (res.status === 400 || res.status === 401) {
                        throw new Error(
                            'This sign-in link is invalid or has expired. Please request a new one.',
                        );
                    }
                    if (res.status !== 200) {
                        throw new Error(
                            'Unable to sign in. Please open the email link again or request a new one.',
                        );
                    }
                    return res.json();
                });
        }
        verificationRequest.current
            .then((data) => {
                if (active) completeLogin(data);
            })
            .catch((err) => {
                if (active)
                    setError(err.message || 'Unable to sign in. Please request a new link.');
            })
            .finally(() => {
                if (active) setVerifying(false);
            });
        return () => {
            active = false;
        };
    }, [emailToken, completeLogin, setError]);

    return verifying;
}

function EmailConfirmation({ email, error, loading, resendAvailableAt, onResend, onChangeEmail }) {
    const [secondsRemaining, setSecondsRemaining] = useState(() =>
        Math.max(0, Math.ceil((resendAvailableAt - Date.now()) / 1000)),
    );
    const focusTitle = useCallback((node) => node?.focus(), []);
    useEffect(() => {
        const updateCountdown = () => {
            const remaining = Math.max(0, Math.ceil((resendAvailableAt - Date.now()) / 1000));
            setSecondsRemaining(remaining);
            if (remaining === 0) clearInterval(timer);
        };
        const timer = setInterval(updateCountdown, 1000);
        updateCountdown();
        return () => clearInterval(timer);
    }, [resendAvailableAt]);

    return (
        <>
            <CardTitle component="h2" tabIndex={-1} ref={focusTitle}>
                Check your email
            </CardTitle>
            <CardSubtitle sx={{ overflowWrap: 'anywhere' }}>
                If an account exists for {email}, we’ve sent a sign-in link.
            </CardSubtitle>
            <CardSubtitle>
                Open the link in your email on the device where you want to sign in to your
                FrontDoor+ Dashboard.
            </CardSubtitle>
            <CardSubtitle>Didn’t get the email? Check your spam or junk folder.</CardSubtitle>
            {error && <ErrorText role="alert">{error}</ErrorText>}
            <ResendButton
                fullWidth
                variant="outlined"
                disabled={loading || secondsRemaining > 0}
                onClick={onResend}
            >
                {loading
                    ? 'Sending…'
                    : secondsRemaining > 0
                      ? `Resend email (${secondsRemaining}s)`
                      : 'Resend email'}
            </ResendButton>
            <HelperText>
                {secondsRemaining > 0
                    ? `You can request another email in ${secondsRemaining} seconds.`
                    : 'You can now request another email.'}
            </HelperText>
            <ModeLink component="button" type="button" disabled={loading} onClick={onChangeEmail}>
                Use a different email
            </ModeLink>
        </>
    );
}

const Page = styled(Box)({
    minHeight: '100vh',
    backgroundColor: '#0b1013',
    color: '#f4f7f8',
    display: 'flex',
    flexDirection: 'column',
});

const Header = styled('header')(({ theme }) => ({
    borderBottom: '1px solid #232b31',
    padding: theme.spacing(2.25, 0),
}));

const Logo = styled('img')({
    display: 'block',
    height: 32,
    maxWidth: '100%',
});

const Content = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 520px) minmax(380px, 1fr)',
    gap: theme.spacing(12),
    alignItems: 'start',
    paddingTop: theme.spacing(7),
    paddingBottom: theme.spacing(8),
    [theme.breakpoints.down('md')]: {
        gridTemplateColumns: '1fr',
        gap: theme.spacing(5),
        paddingTop: theme.spacing(5),
    },
}));

const Intro = styled(Box)(({ theme }) => ({
    paddingTop: theme.spacing(0.5),
}));

const HeroTitle = styled(Typography)(({ theme }) => ({
    maxWidth: 560,
    color: '#f8fafb',
    fontSize: 42,
    fontWeight: 800,
    lineHeight: 1.1,
    letterSpacing: 0,
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
        fontSize: 34,
    },
}));

const HeroCopy = styled(Typography)({
    maxWidth: 555,
    color: '#a8b0b7',
    fontSize: 18,
    lineHeight: 1.55,
});

const FeatureChips = styled(Stack)(({ theme }) => ({
    marginTop: theme.spacing(4),
    maxWidth: 460,
}));

const FeatureChip = styled(Chip)(({ theme }) => ({
    height: 34,
    borderRadius: 17,
    border: '1px solid #29323a',
    backgroundColor: '#151b20',
    color: '#b3bbc1',
    fontSize: 16,
    '& .MuiChip-label': {
        paddingLeft: theme.spacing(1.5),
        paddingRight: theme.spacing(1.5),
    },
}));

const DividerStyled = styled(Divider)(({ theme }) => ({
    marginTop: theme.spacing(24),
    marginBottom: theme.spacing(4),
    borderColor: '#273039',
    [theme.breakpoints.down('md')]: {
        marginTop: theme.spacing(7),
    },
}));

const NewAccount = styled(Box)({
    maxWidth: 540,
});

const SectionTitle = styled(Typography)(({ theme }) => ({
    color: '#f8fafb',
    fontSize: 22,
    fontWeight: 800,
    marginBottom: theme.spacing(2),
}));

const SectionCopy = styled(Typography)({
    color: '#a8b0b7',
    fontSize: 16,
    lineHeight: 1.55,
    marginBottom: 24,
});

const SecondaryButton = styled(Button)(({ theme }) => ({
    color: '#f4f7f8',
    borderColor: '#2d363d',
    borderRadius: 4,
    padding: theme.spacing(1.05, 2.3),
    fontSize: 15,
    fontWeight: 700,
    textTransform: 'none',
    '&:hover': {
        borderColor: '#3c474f',
        backgroundColor: '#151b20',
    },
}));

const CardRoot = styled(Paper)(({ theme }) => ({
    width: '100%',
    maxWidth: 560,
    justifySelf: 'end',
    backgroundColor: '#151b20',
    border: '1px solid #29323a',
    borderRadius: 8,
    color: '#f4f7f8',
    padding: theme.spacing(4.5),
    [theme.breakpoints.down('md')]: {
        justifySelf: 'start',
    },
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(3),
    },
}));

const CardTitle = styled(Typography)(({ theme }) => ({
    color: '#f8fafb',
    fontSize: 24,
    fontWeight: 800,
    lineHeight: 1.2,
    marginBottom: theme.spacing(1),
}));

const CardSubtitle = styled(Typography)(({ theme }) => ({
    color: '#a8b0b7',
    fontSize: 16,
    lineHeight: 1.4,
    marginBottom: theme.spacing(4),
}));

const Form = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
});

const Label = styled(Typography)(({ theme }) => ({
    color: '#e6eaed',
    fontSize: 15,
    fontWeight: 800,
    marginBottom: theme.spacing(1),
}));

const AuthTextField = styled(TextField)(({ theme }) => ({
    marginBottom: theme.spacing(3),
    '& .MuiOutlinedInput-root': {
        color: '#f4f7f8',
        backgroundColor: '#0b1013',
        borderRadius: 4,
        '& fieldset': {
            borderColor: '#29323a',
        },
        '&:hover fieldset': {
            borderColor: '#3a454d',
        },
        '&.Mui-focused fieldset': {
            borderColor: '#34cf91',
        },
        '&.Mui-error fieldset': {
            borderColor: '#f06d6d',
        },
    },
    '& .MuiOutlinedInput-input': {
        padding: theme.spacing(1.55, 1.75),
        fontSize: 16,
        '&::placeholder': {
            color: '#9fa8af',
            opacity: 1,
        },
    },
}));

const SessionExpiredBanner = styled(Typography)(({ theme }) => ({
    backgroundColor: '#221c12',
    border: '1px solid #8a6d3b',
    borderRadius: 4,
    color: '#f0c987',
    fontSize: 14,
    fontWeight: 700,
    lineHeight: 1.45,
    marginBottom: theme.spacing(3),
    padding: theme.spacing(1.5, 2),
}));

const ErrorText = styled(Typography)(({ theme }) => ({
    color: '#ff8b8b',
    fontSize: 14,
    fontWeight: 700,
    marginTop: theme.spacing(-1.5),
    marginBottom: theme.spacing(2),
}));

const InlineLink = styled(Link)({
    color: '#34cf91',
    fontWeight: 700,
    marginBottom: 24,
    display: 'inline-flex',
    alignSelf: 'flex-start',
    '&:hover': {
        color: '#58dfaa',
    },
});

const ModeLink = styled(InlineLink)({
    marginTop: 16,
    marginBottom: 0,
    textAlign: 'left',
    '&:disabled': { opacity: 0.5, cursor: 'default' },
});

const HelperText = styled(Typography)({
    color: '#a8b0b7',
    fontSize: 14,
    marginTop: 8,
    marginBottom: 24,
});

const ResendButton = styled(SecondaryButton)({
    minHeight: 50,
    '&.Mui-disabled': {
        color: '#9fa8af',
        backgroundColor: '#252d33',
        borderColor: '#69737b',
    },
});

const PrimaryButton = styled(Button)({
    backgroundColor: '#34cf91',
    borderRadius: 4,
    color: '#07100d',
    fontSize: 15,
    fontWeight: 800,
    minHeight: 50,
    textTransform: 'none',
    '&:hover': {
        backgroundColor: '#58dfaa',
    },
});

const Footer = styled(Box)(({ theme }) => ({
    borderTop: '1px solid #232b31',
    padding: theme.spacing(2.7, 0),
}));

const FooterInner = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
    },
}));

const FooterText = styled(Typography)({
    color: '#9fa8af',
    fontSize: 14,
});

const FooterLink = styled(Link)({
    color: '#9fa8af',
    fontSize: 14,
});
